import { useTranslation } from 'react-i18next';

import type { Role } from '../../lib/api/users';
import styles from './RoleCheckboxes.module.css';

/**
 * The role picker inside `UserFormDialog`'s own form.
 *
 * FULLY CONTROLLED. This component owns no state at all — `value` is the
 * complete set of held role names and `onChange` always emits the complete
 * replacement set, never a delta. That mirrors the endpoint it feeds:
 * `PUT /users/{uuid}/roles` REPLACES the assignment and validates `roles` as
 * `present|array`, so unticking every role is not a no-op, it is how a role
 * is removed, and the empty array it produces is a legitimate body rather
 * than something to special-case away.
 *
 * ORDER COMES FROM `roles`, NOT FROM TICK ORDER. `toggle` derives the next
 * array by filtering the CATALOGUE (`roles`) down to whichever names are now
 * held, rather than appending the just-ticked name to `value`. Appending
 * would make the emitted order depend on the sequence an administrator
 * happened to click in, which is not a property either caller or test should
 * have to account for.
 *
 * LABELLED BY THE CATALOGUE'S OWN TRANSLATION, NEVER BY `name`. `name` is the
 * machine string a policy check reads (`user.role.assign` and so on); an
 * administrator reads `label_en`/`label_ar`, chosen by `i18n.language` the
 * same way `UserListScreen.roleLabel` already does for the read-only column.
 *
 * THE MFA WARNING IS A CONSEQUENCE, NOT A VALIDATION ERROR. `mfa_required` on
 * a role means assigning it will require the account to enrol two-step
 * verification — worth saying here, because the alternative is an
 * administrator finding out from a 409 on a completely different screen
 * (`UserActions.tsx`'s `mfaRoleRequired` message is exactly that 409). The
 * line is mounted from the first frame, empty, and filled only while such a
 * role is currently ticked — the same "live region the platform is already
 * watching" shape every other notice in this module uses.
 */

export interface RoleCheckboxesProps {
  /** The catalogue. The parent fetches it; this component only renders. */
  roles: readonly Role[];
  /** Currently held role names. */
  value: readonly string[];
  onChange: (names: readonly string[]) => void;
}

export function RoleCheckboxes({ roles, value, onChange }: RoleCheckboxesProps) {
  const { t, i18n } = useTranslation();

  function roleLabel(role: Role): string {
    return i18n.language.startsWith('ar') ? role.label_ar : role.label_en;
  }

  function toggle(name: string, checked: boolean): void {
    const held = new Set(value);
    if (checked) held.add(name);
    else held.delete(name);

    onChange(roles.filter((role) => held.has(role.name)).map((role) => role.name));
  }

  const warns = roles.some((role) => role.mfa_required && value.includes(role.name));

  return (
    <fieldset className={styles['group']}>
      <legend className={styles['legend']}>{t('admin.rolesTitle')}</legend>

      <div className={styles['list']}>
        {roles.map((role) => (
          <label key={role.name} className={styles['option']}>
            <input
              type="checkbox"
              className={styles['checkbox']}
              checked={value.includes(role.name)}
              onChange={(event) => {
                toggle(role.name, event.target.checked);
              }}
            />
            {roleLabel(role)}
          </label>
        ))}
      </div>

      <p className={styles['warning']} role="status" aria-atomic="true" data-testid="rolesMfaWarning">
        {warns ? t('admin.rolesMfaWarning') : null}
      </p>
    </fieldset>
  );
}
